import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

/** Lokale datum-key (YYYY-MM-DD) — bewust geen toISOString i.v.m. tijdzones. */
export const toDateKey = (date: Date): string => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

interface RecordingsCalendarProps {
    /** Aantal opnames per dag, keyed op YYYY-MM-DD */
    counts: Record<string, number>;
    selectedDate: string | null;
    onSelectDate: (date: string | null) => void;
}

/**
 * Maandkalender met opname-badges per dag (Redesign 2026).
 * Dagen met opnames zijn klikbaar en filteren de lijst.
 */
const RecordingsCalendar: React.FC<RecordingsCalendarProps> = ({ counts, selectedDate, onSelectDate }) => {
    const { t, i18n } = useTranslation();
    const today = useMemo(() => new Date(), []);
    const [monthCursor, setMonthCursor] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));

    const cells = useMemo(() => {
        const year = monthCursor.getFullYear();
        const month = monthCursor.getMonth();
        const startOffset = (new Date(year, month, 1).getDay() + 6) % 7; // maandag = 0
        return Array.from({ length: 42 }, (_, i) => {
            const date = new Date(year, month, i + 1 - startOffset);
            return {
                date,
                key: toDateKey(date),
                inMonth: date.getMonth() === month,
            };
        });
    }, [monthCursor]);

    const monthLabel = useMemo(() => {
        const label = monthCursor.toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' });
        return label.charAt(0).toUpperCase() + label.slice(1);
    }, [monthCursor, i18n.language]);

    const todayKey = toDateKey(today);

    const shiftMonth = (delta: number) => {
        setMonthCursor(prev => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
    };

    const goToToday = () => {
        setMonthCursor(new Date(today.getFullYear(), today.getMonth(), 1));
        onSelectDate(counts[todayKey] ? todayKey : null);
    };

    return (
        <div className="rounded-2xl border border-primary-100 bg-container p-5 shadow-soft">
            <div className="mb-4 flex items-center justify-between gap-2">
                <span className="text-sm font-semibold" style={{ color: 'var(--text-strong)' }}>{monthLabel}</span>
                <div className="flex items-center gap-1.5">
                    <button
                        onClick={goToToday}
                        className="rounded-lg border border-primary-100 bg-paper px-3 py-1.5 text-xs font-semibold text-primary-700 transition-colors hover:border-primary-200 hover:bg-primary-50"
                    >
                        {t('recordingsCalendar.today')}
                    </button>
                    <button
                        onClick={() => shiftMonth(-1)}
                        className="rounded-lg border border-primary-100 bg-paper p-1.5 text-primary-500 transition-colors hover:border-primary-200 hover:bg-primary-50"
                        title={t('recordingsCalendar.previousMonth')}
                    >
                        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M15 18l-6-6 6-6" />
                        </svg>
                    </button>
                    <button
                        onClick={() => shiftMonth(1)}
                        className="rounded-lg border border-primary-100 bg-paper p-1.5 text-primary-500 transition-colors hover:border-primary-200 hover:bg-primary-50"
                        title={t('recordingsCalendar.nextMonth')}
                    >
                        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M9 6l6 6-6 6" />
                        </svg>
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-7 gap-1.5">
                {WEEKDAY_KEYS.map(day => (
                    <span key={day} className="py-1 text-center text-[0.66rem] font-bold uppercase" style={{ color: 'var(--text-muted)' }}>
                        {t(`recordingsCalendar.weekdays.${day}`)}
                    </span>
                ))}
                {cells.map(({ date, key, inMonth }) => {
                    const count = counts[key] ?? 0;
                    const isSelected = selectedDate === key;
                    const isToday = key === todayKey;
                    const isFuture = key > todayKey; // YYYY-MM-DD sorteert lexicografisch als datum
                    const clickable = count > 0;

                    let cellClasses = 'bg-paper border-primary-100 text-gray-800';
                    if (!inMonth) {
                        cellClasses = 'border-transparent bg-transparent';
                    } else if (isSelected) {
                        cellClasses = 'border-primary-700 bg-primary-700 text-white';
                    } else if (count > 0) {
                        cellClasses = 'bg-background border-primary-200 text-gray-800 hover:border-primary-300';
                    } else if (isFuture) {
                        // Toekomstige dagen kunnen nooit opnames bevatten → expliciete inactive-state
                        cellClasses = 'border-transparent bg-transparent opacity-40';
                    }

                    return (
                        <button
                            key={key}
                            onClick={() => clickable && onSelectDate(isSelected ? null : key)}
                            disabled={!clickable}
                            className={`relative flex h-9 items-center justify-center rounded-lg border text-[0.82rem] transition-colors ${cellClasses} ${isToday ? 'font-bold' : ''} ${clickable ? 'cursor-pointer' : 'cursor-default'}`}
                            style={!inMonth ? { color: 'var(--calendar-day-outside-text)' } : undefined}
                            title={count > 0 ? t('recordingsCalendar.recordingCount', { count }) : undefined}
                        >
                            {date.getDate()}
                            {count > 0 && inMonth && (
                                <span
                                    className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[0.62rem] font-bold text-white shadow-soft"
                                    style={{ background: 'var(--calendar-count-bg)', border: '1.5px solid var(--container)' }}
                                >
                                    {count}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default RecordingsCalendar;
